import React, { useMemo, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import * as Bixby from '$/core';
import NotFound from '$/pages/not-found';
import { AgGridReact } from 'ag-grid-react';
import {
  getIsCurrentUserAdminState,
  getIsCurrentUserCreditSightsAnalystState,
  getIsCurrentUserCreditSightsOpsState,
  getIsCurrentUserRatingsAnalystState,
  getIsCurrentUserSuFAnalystState,
} from '$/store/profile/selectors';
import { getHasPublicCompaniesFeatureFlagState } from '$/store/login/selectors';
import dayjs from 'dayjs';
import {
  exportHistoricalTearSheet,
  exportHistoricalPrimer,
  exportHistoricalFinancialModel,
} from '$/store/export/actions';
import './index.scss';
import { CompanyService } from '$/services';
import Loading from '$/components/loading/Loading';
import { GridApi, ColumnApi } from 'ag-grid-enterprise';
import HeadingBlock from '$/components/global/heading';
import { getCompanyHistoryFeatureFlagState } from '$/store/release-feature-flags/selectors';
import NoDataFound from '$/components/global/noData';
import { exportTearSheet, exportPrimer, exportFinancialModel } from '$/store/export/actions';
import { getHistoricalFinancialModelsEnabled } from '$/store/release-feature-flags/selectors';

interface ICompanyDocumentsHistoryProps {
  featureFlags?: string[];
  isAdmin: boolean;
  isCreditSightsOpsSufOrRatings: boolean;
  hasPublicCompaniesFeatureFlag: boolean;
  isCompanyHistoryEnabled: boolean;
  isHistoricalFinancialModelsEnabled: boolean;
  dispatchDownloadHistoricalTearSheet: (companyId: string, companyName: string) => void;
  dispatchDownloadHistoricalPrimer: (companyId: string, companyName: string) => void;
  dispatchDownloadHistoricalFinancialModel: (companyId: string, companyName: string) => Promise<void>;
  dispatchDownloadTearSheet: (company: Bixby.Models.CompanyExport) => Promise<void>;
  dispatchDownloadPrimer: (company: Bixby.Models.CompanyExport) => Promise<void>;
  dispatchDownloadFinancialModel: (companyId: string, companyName: string) => Promise<void>;
}

const CompanyDocumentsHistory: React.FC<ICompanyDocumentsHistoryProps> = (props: ICompanyDocumentsHistoryProps) => {
  const {
    featureFlags,
    isAdmin,
    isCreditSightsOpsSufOrRatings,
    hasPublicCompaniesFeatureFlag,
    isCompanyHistoryEnabled,
    isHistoricalFinancialModelsEnabled,
    dispatchDownloadHistoricalFinancialModel,
    dispatchDownloadHistoricalTearSheet,
    dispatchDownloadHistoricalPrimer,
    dispatchDownloadFinancialModel,
    dispatchDownloadTearSheet,
    dispatchDownloadPrimer,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [companyHistories, setCompanyHistories] = useState<Bixby.Models.CompanyHistory[]>([]);
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);

  const isAuthorizedToView = useMemo(
    () => featureFlags && featureFlags.includes(Bixby.Constants.CLIENT_COMPANY_DOCUMENTS) && isCompanyHistoryEnabled,
    [featureFlags, isCompanyHistoryEnabled]
  );

  const isAdminOrCreditSightsOpsSufOrRatings = isAdmin || isCreditSightsOpsSufOrRatings;

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);
        const results = await CompanyService.getHistoricalCompanies();
        setCompanyHistories(results);
      } catch (error: any) {
        // suppress errors
      } finally {
        setIsLoading(false);
      }
    };
    if (isAuthorizedToView) {
      getData();
    }
  }, [isAuthorizedToView]);

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  if (!isAuthorizedToView) {
    return <NotFound />;
  }

  const getContextMenuItems = (params: any) => {
    if (params.column.colId === 'uniqueName' || params.column.colId === 'companyName') {
      return [];
    }

    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  return (
    <div>
      <HeadingBlock title="Company Documents History" />
      <section className="company-documents-history">
        {isLoading ? (
          <Loading pageLevel />
        ) : companyHistories ? (
          <div className="grid_container">
            <div className="grid ag-theme-alpine">
              <AgGridReact
                enableRangeSelection={true}
                getContextMenuItems={getContextMenuItems}
                defaultColDef={{
                  flex: 1,
                  minWidth: 100,
                  sortable: true,
                  resizable: true,
                  lockPinned: true,
                  filter: 'agNumberColumnFilter',
                  cellClass: 'align-center',
                }}
                rowData={companyHistories}
                onGridReady={onGridReady}
                suppressDragLeaveHidesColumns={true}
                suppressMovableColumns={true}
                animateRows={true}
                rowHeight={32}
                headerHeight={32}
                gridOptions={{
                  getRowClass: params => (params.data?.['isActive'] ? '' : 'inactive'),
                }}
                columnDefs={[
                  {
                    field: 'uniqueName',
                    hide: !isAdminOrCreditSightsOpsSufOrRatings,
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                    headerName: 'Company',
                    cellRenderer: (params: any) => {
                      const url = '/company/' + params.data['companyId'];
                      return (
                        <a className="fake_link" href={url}>
                          {' '}
                          {params.data['uniqueName']}
                        </a>
                      );
                    },
                    minWidth: 250,
                    pinned: 'left',
                    cellClass: 'align-left',
                  },
                  {
                    field: 'companyName',
                    hide: isAdminOrCreditSightsOpsSufOrRatings,
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                    headerName: 'Company',
                    cellRenderer: (params: any) => {
                      if (params.data['isActive']) {
                        return (
                          <a className="fake_link" href={`/company/${params.data['companyId']}`}>
                            {params.data['companyName']}
                          </a>
                        );
                      } else {
                        return params.data['companyName'];
                      }
                    },
                    minWidth: 250,
                    pinned: 'left',
                    cellClass: 'align-left',
                  },
                  {
                    field: 'loanTicker',
                    headerName: 'Loan Ticker',
                    maxWidth: 150,
                    cellClass: 'align-left',
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                  },
                  {
                    maxWidth: 150,
                    cellRenderer: (params: any) => {
                      if (params.data['isActive']) {
                        return (
                          <a className="fake_link" href={`/company/${params.data['companyId']}/documents`}>
                            Documents
                          </a>
                        );
                      } else {
                        return (
                          <a className="fake_link" href={`/company-documents/${params.data['companyId']}`}>
                            Documents
                          </a>
                        );
                      }
                    },
                  },
                  {
                    maxWidth: 125,
                    hide: !isHistoricalFinancialModelsEnabled,
                    onCellClicked: event => {
                      const company: Bixby.Models.CompanyHistory | undefined = event.data;
                      if (!company || company.doesNotHaveFinancials) {
                        return;
                      }
                      if (company.isActive) {
                        dispatchDownloadFinancialModel(company.companyId, company.companyName);
                      } else if (company) {
                        dispatchDownloadHistoricalFinancialModel(company.companyId, company.companyName);
                      }
                    },
                    cellRenderer: params => {
                      if (params.data['doesNotHaveFinancials']) {
                        return 'Not Available';
                      }
                      return (
                        <div className="fake_link" role="button">
                          Financial Model
                        </div>
                      );
                    },
                  },
                  {
                    maxWidth: 125,
                    onCellClicked: event => {
                      const data: Bixby.Models.CompanyHistory | undefined = event.data;
                      if (data?.canDownloadTearsheetAndPrimer) {
                        if (data.isActive) {
                          dispatchDownloadTearSheet({
                            id: data.companyId,
                            name: data.companyName,
                          });
                        } else {
                          dispatchDownloadHistoricalTearSheet(data.companyId, data.companyName);
                        }
                      }
                    },
                    cellRenderer: params => {
                      if (params.data['canDownloadTearsheetAndPrimer'] === false) {
                        return 'Not Available';
                      }
                      return (
                        <div className="fake_link" role="button">
                          Tearsheet
                        </div>
                      );
                    },
                  },
                  {
                    maxWidth: 125,
                    onCellClicked: event => {
                      const data: Bixby.Models.CompanyHistory | undefined = event.data;
                      if (data?.canDownloadTearsheetAndPrimer) {
                        if (data.isActive) {
                          dispatchDownloadPrimer({
                            id: data.companyId,
                            name: data.companyName,
                          });
                        } else {
                          dispatchDownloadHistoricalPrimer(data.companyId, data.companyName);
                        }
                      }
                    },
                    cellRenderer: params => {
                      if (params.data['canDownloadTearsheetAndPrimer'] === false) {
                        return 'Not Available';
                      }
                      return (
                        <div className="fake_link" role="button">
                          Primer
                        </div>
                      );
                    },
                  },
                  {
                    headerName: 'Ownership',
                    hide: !hasPublicCompaniesFeatureFlag,
                    field: 'isSECPublic',
                    minWidth: 130,
                    cellClass: 'align-left',
                    cellRenderer: (params: any) => (params.data['isSECPublic'] ? 'Public' : 'Private'),
                    filter: 'agTextColumnFilter',
                    filterParams: { valueGetter: (params: any) => (params.data['isSECPublic'] ? 'Public' : 'Private') },
                  },
                  {
                    field: 'industryGroupName',
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                    headerName: 'Industry Group',
                    minWidth: 250,
                    cellClass: 'align-left',
                  },
                  {
                    field: 'dataSiteName',
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                    headerName: 'Data Site Name',
                    minWidth: 250,
                    cellClass: 'align-left',
                  },
                  {
                    field: 'lastAccessDate',
                    headerName: 'Access Status',
                    valueGetter: params =>
                      params.data?.['isActive']
                        ? 'Current'
                        : `Lost Access ${Bixby.Utils.formatDateForByline(dayjs.utc(params.data?.['lastAccessDate']))}`,
                    minWidth: 100,
                    filter: 'agTextColumnFilter',
                    hide: isCreditSightsOpsSufOrRatings,
                  },
                ]}></AgGridReact>
            </div>
          </div>
        ) : (
          <NoDataFound dataType="company documents" />
        )}
      </section>
    </div>
  );
};

// map state to props for feature flags
const mapStateToProps = (state: AppState) => {
  const { isAdmin } = getIsCurrentUserAdminState(state);
  const { hasPublicCompaniesFeatureFlag } = getHasPublicCompaniesFeatureFlagState(state);
  const { isCompanyHistoryEnabled } = getCompanyHistoryFeatureFlagState(state);
  const { isCreditSightsAnalyst } = getIsCurrentUserCreditSightsAnalystState(state);
  const { isSuFAnalyst } = getIsCurrentUserSuFAnalystState(state);
  const { isCreditSightsOps } = getIsCurrentUserCreditSightsOpsState(state);
  const { isHistoricalFinancialModelsEnabled } = getHistoricalFinancialModelsEnabled(state);
  const { isRatingsAnalyst } = getIsCurrentUserRatingsAnalystState(state);

  return {
    featureFlags: state.loginState.featureFlags,
    isAdmin,
    hasPublicCompaniesFeatureFlag,
    isCompanyHistoryEnabled,
    isHistoricalFinancialModelsEnabled,
    isCreditSightsOpsSufOrRatings: isCreditSightsAnalyst || isCreditSightsOps || isSuFAnalyst || isRatingsAnalyst,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchDownloadHistoricalFinancialModel: (companyId: string, companyName: string) =>
    dispatch(exportHistoricalFinancialModel(companyId, companyName)),
  dispatchDownloadHistoricalTearSheet: (companyId: string, companyName: string) =>
    dispatch(exportHistoricalTearSheet(companyId, companyName)),
  dispatchDownloadHistoricalPrimer: (companyId: string, companyName: string) =>
    dispatch(exportHistoricalPrimer(companyId, companyName)),
  dispatchDownloadFinancialModel: (companyId: string, companyName: string) =>
    dispatch(exportFinancialModel(companyId, companyName)),
  dispatchDownloadTearSheet: (company: Bixby.Models.CompanyExport) => dispatch(exportTearSheet(company)),
  dispatchDownloadPrimer: (company: Bixby.Models.CompanyExport) => dispatch(exportPrimer(company)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyDocumentsHistory);
